import { useState, useEffect } from 'react';
import {
  listCases,
  Case,
  getCaseDetail,
  getCaseTimeline,
  getCaseTeams,
  CaseDetail,
  TimelineEvent,
  Team,
  getBillingSummary,
  getBillingItems,
  BillingSummary,
  BillingItem,
} from '../services/api';

export const useCases = () => {
  // Cases List State
  const [cases, setCases] = useState<Case[]>([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [casesError, setCasesError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [highestPage, setHighestPage] = useState(1);

  // Case Detail State
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [caseDetail, setCaseDetail] = useState<CaseDetail | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [caseDetailLoading, setCaseDetailLoading] = useState(false);
  const [activeTabDetail, setActiveTabDetail] = useState<
    'notes' | 'documents' | 'contacts' | 'teams' | 'billing' | 'timeline'
  >('notes');
  const [teams, setTeams] = useState<Team[]>([]);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);

  // Restore state from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const caseId = params.get('caseId');
    const tab = params.get('tab') as 'notes' | 'documents' | 'contacts' | 'teams' | 'billing' | 'timeline' | null;

    if (caseId) {
      setSelectedCaseId(caseId);
      if (tab) {
        setActiveTabDetail(tab);
      }
      handleCaseClickInternal(caseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL when case or tab changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    if (selectedCaseId) {
      params.set('caseId', selectedCaseId);
      params.set('tab', activeTabDetail);
    } else {
      params.delete('caseId');
      params.delete('tab');
    }

    const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [selectedCaseId, activeTabDetail]);

  // Load cases on mount and when filter changes
  useEffect(() => {
    setCurrentPage(1);
    setHighestPage(1);
    loadCases(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadCases = async (page: number = 1) => {
    try {
      setCasesLoading(true);
      setCasesError(null);

      const offset = (page - 1) * pageSize;

      const response = await listCases({
        limit: pageSize,
        status: statusFilter || undefined,
        offset,
      });

      setCases(response.cases);
      setHasMore(response.hasMore);
      setCurrentPage(page);

      if (page > highestPage) {
        setHighestPage(page);
      }
    } catch (err: any) {
      setCasesError(err.response?.data?.error || 'Failed to load cases');
    } finally {
      setCasesLoading(false);
    }
  };

  const handleCaseClickInternal = async (caseId: string) => {
    setCaseDetailLoading(true);
    try {
      const [detail, timelineResponse, teamsData] = await Promise.all([
        getCaseDetail(caseId),
        getCaseTimeline(caseId),
        getCaseTeams(caseId),
      ]);
      setCaseDetail(detail);
      setTimeline(timelineResponse.timeline);
      setTeams(teamsData.teams || []);
    } catch (error) {
      console.error('Error loading case details:', error);
      alert('Failed to load case details');
    } finally {
      setCaseDetailLoading(false);
    }
  };

  const handleCaseClick = async (caseId: string) => {
    setSelectedCaseId(caseId);
    await handleCaseClickInternal(caseId);
  };

  const handleTabChangeDetail = async (
    tab: 'notes' | 'documents' | 'contacts' | 'teams' | 'billing' | 'timeline'
  ) => {
    setActiveTabDetail(tab);
    // Lazy load billing data
    if (tab === 'billing' && selectedCaseId && !billingSummary) {
      await loadBillingData(selectedCaseId);
    }
  };

  const loadBillingData = async (caseId: string) => {
    setBillingLoading(true);
    try {
      const [summary, items] = await Promise.all([
        getBillingSummary(caseId),
        getBillingItems(caseId),
      ]);
      setBillingSummary(summary);
      setBillingItems(items.items || []);
    } catch (error) {
      console.error('Error loading billing data:', error);
      alert('Failed to load billing data');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleBackToCases = () => {
    setSelectedCaseId(null);
    setCaseDetail(null);
    setTimeline([]);
    setTeams([]);
    setBillingSummary(null);
    setBillingItems([]);
    setActiveTabDetail('notes');
  };

  const refreshCaseDetail = async () => {
    if (!selectedCaseId) return;
    const detail = await getCaseDetail(selectedCaseId);
    setCaseDetail(detail);
  };

  const refreshTimeline = async () => {
    if (!selectedCaseId) return;
    const timelineResponse = await getCaseTimeline(selectedCaseId);
    setTimeline(timelineResponse.timeline);
  };

  return {
    // Cases List
    cases,
    setCases,
    casesLoading,
    casesError,
    statusFilter,
    setStatusFilter,
    searchTerm,
    setSearchTerm,
    currentPage,
    pageSize,
    hasMore,
    highestPage,
    loadCases,

    // Case Detail
    selectedCaseId,
    setSelectedCaseId,
    caseDetail,
    setCaseDetail,
    timeline,
    setTimeline,
    caseDetailLoading,
    activeTabDetail,
    teams,
    setTeams,
    billingSummary,
    billingItems,
    billingLoading,

    // Handlers
    handleCaseClick,
    handleTabChangeDetail,
    handleBackToCases,
    refreshCaseDetail,
    refreshTimeline,
  };
};

